/**
 * Minimum Order Value Checkout Validation
 *
 * This file handles minimum order value requirements for checkout.
 * To change the minimum order value, update MINIMUM_ORDER_VALUE below.
 */

class MinimumQuantityCheckout {
  // CONFIGURATION - Change this value to update minimum order value requirement (in your currency's main unit, e.g. rupees)
  static MINIMUM_ORDER_VALUE = 400;

  /**
   * Get total cart value (in main currency unit, not cents/paise)
   * @param {Object} cartData - Optional cart data from events
   * @returns {number} Total cart value
   */
  static async getCartTotal(cartData = null) {
    if (cartData && cartData.total_price !== undefined) {
      this._cartReq = Promise.resolve(cartData);
      this._cartReqAt = Date.now();
      return cartData.total_price / 100;
    }

    try {
      // Share one /cart.js request between all buttons checked in the same moment.
      const now = Date.now();
      if (!this._cartReq || now - this._cartReqAt > 1200) {
        this._cartReqAt = now;
        this._cartReq = fetch('/cart.js', { credentials: 'same-origin' }).then((r) => r.json());
      }
      const cart = await this._cartReq;
      if (cart && cart.total_price !== undefined) {
        return cart.total_price / 100;
      }
    } catch (error) {
      this._cartReq = null;
      console.error('Failed to fetch cart data:', error);
    }

    if (window.cart && window.cart.total_price !== undefined) {
      return window.cart.total_price / 100;
    }

    return 0;
  }

  static async meetsMinimumRequirement(cartData = null) {
    const total = await this.getCartTotal(cartData);
    return total >= this.MINIMUM_ORDER_VALUE;
  }

  static async getAmountNeeded(cartData = null) {
    const total = await this.getCartTotal(cartData);
    const needed = this.MINIMUM_ORDER_VALUE - total;
    return needed > 0 ? needed : 0;
  }

  static async updateCheckoutButton(button, cartData = null) {
    if (!button) return;

    const cartTotal = await this.getCartTotal(cartData);
    const isValid = cartTotal >= this.MINIMUM_ORDER_VALUE;
    const amountNeeded = this.MINIMUM_ORDER_VALUE - cartTotal;

    const buttonText = button.querySelector('span') || button;

    const currentState = button.dataset.checkoutState;
    const newState = !isValid && cartTotal > 0 ? `disabled-${cartTotal}` : cartTotal > 0 ? 'enabled' : 'empty';

    if (currentState !== newState) {
      button.dataset.checkoutState = newState;

      if (!isValid && cartTotal > 0) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');

        if (!buttonText.dataset.originalText) {
          buttonText.dataset.originalText = buttonText.textContent.trim();
        }

        buttonText.textContent = `Add ₹${amountNeeded.toFixed(2)} more (Min order: ₹${this.MINIMUM_ORDER_VALUE})`;
      } else if (cartTotal > 0) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');

        if (buttonText.dataset.originalText) {
          buttonText.textContent = buttonText.dataset.originalText;
        }
      }
    }
  }

  static async updateBuyNowButton(button, cartData = null) {
    if (!button) return;

    const cartTotal = await this.getCartTotal(cartData);
    const isValid = cartTotal >= this.MINIMUM_ORDER_VALUE;
    const amountNeeded = this.MINIMUM_ORDER_VALUE - cartTotal;

    const currentState = button.dataset.minQtyState;
    const newState = isValid ? 'valid' : `invalid-${cartTotal}`;

    if (currentState !== newState) {
      button.dataset.minQtyState = newState;

      if (!isValid) {
        button.disabled = true;
        button.style.opacity = '0.5';
        button.style.cursor = 'not-allowed';
        button.style.filter = 'blur(0.5px)';
        button.style.pointerEvents = 'none';
        button.title = `Add ₹${amountNeeded.toFixed(2)} more to cart first (minimum order value ₹${this.MINIMUM_ORDER_VALUE} required)`;
      } else {
        button.disabled = false;
        button.style.opacity = '';
        button.style.cursor = '';
        button.style.filter = '';
        button.style.pointerEvents = '';
        button.title = '';
      }
    }
  }

  static async validateAllButtons(cartData = null) {
    const mainCheckoutButton = document.getElementById('checkout');
    await this.updateCheckoutButton(mainCheckoutButton, cartData);

    const drawerCheckoutButton = document.getElementById('CartDrawer-Checkout');
    await this.updateCheckoutButton(drawerCheckoutButton, cartData);

    const errorElements = document.querySelectorAll(
      '#cart-minimum-quantity-error, #cart-drawer-minimum-quantity-error'
    );
    errorElements.forEach(el => {
      if (el) el.style.display = 'none';
    });

    const buyNowButtons = document.querySelectorAll(
      '[data-testid*="Checkout"]:not(#checkout):not(#CartDrawer-Checkout), ' +
      '.shopify-payment-button__button, ' +
      'button[name="add"][value*="Buy"], ' +
      '[data-shopify="payment-button"] button'
    );

    for (const button of buyNowButtons) {
      await this.updateBuyNowButton(button, cartData);
    }
  }

  static init() {
    this.validateAllButtons();

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event && event.cartData ? event.cartData : null;
        this._cartReq = null; // cart changed: drop cached /cart.js
        setTimeout(() => { this._cartReq = null; this.validateAllButtons(); }, 1300);
        this.validateAllButtons(cartData);
        setTimeout(() => {
          this.validateAllButtons(cartData);
        }, 100);
      });
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.validateAllButtons());
    }

    // Light re-check for late-loading payment buttons; skipped while the tab is hidden.
    setInterval(() => { if (!document.hidden) this.validateAllButtons(); }, 3000);
  }
}

MinimumQuantityCheckout.init();

window.CartCheckoutValidation = MinimumQuantityCheckout;